import React from 'react'
import {Box, Typography} from '@mui/material'
import moment from 'moment'
import {ColumnDef} from 'src/components-new/data/DataTable'
import {FilterItem} from 'src/components-new/data/Filters'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'

export type MaterialRecord = {
  _id: string
  name?: string
  fileName?: string
  fileId?: string
  category?: string[]
  categoryReadable?: string
  createdAt?: string
  type?: string
  size?: number
  description?: string
  [key: string]: any
}

export type LinkRecord = {
  _id: string
  name?: string
  url?: string
  createdAt?: string
  [key: string]: any
}

type ColumnOption = {id: string; label: string; type?: string; format?: string}

const formatDate = (value?: string, format?: string): string =>
  value ? moment(value).format(format ?? 'DD MMMM YYYY, HH:mm') : '—'

// ── Materials ──────────────────────────────────────────────────────────────

interface MaterialRowActionsProps {
  row: MaterialRecord
  onInfo: (row: MaterialRecord) => void
  onView: (row: MaterialRecord) => void
}

function MaterialRowActions({row, onInfo, onView}: MaterialRowActionsProps) {
  return (
    <Box onClick={(e) => e.stopPropagation()} sx={{display: 'flex', gap: 0.5}}>
      <ActionIconButton variant="info" tooltip="Info file" onClick={() => onInfo(row)} />
      <ActionIconButton variant="download" tooltip="Scarica" onClick={() => onView(row)} />
    </Box>
  )
}

interface BuildMaterialsColumnsOptions {
  onInfo: (row: MaterialRecord) => void
  onView: (row: MaterialRecord) => void
  columnOptions?: ColumnOption[]
}

const renderMaterialCell = (col: ColumnOption) => (row: MaterialRecord): React.ReactNode => {
  const {id} = col
  if (id === 'createdAt' || col.type === 'date') {
    return <Typography variant="body2">{formatDate((row as any)[id] ?? row.createdAt, col.format)}</Typography>
  }
  if (id === 'category') {
    return <Typography variant="body2">{row.categoryReadable ?? '—'}</Typography>
  }
  if (id === 'name') {
    return <Typography variant="body2">{row.name ?? row.fileName ?? '—'}</Typography>
  }
  return <Typography variant="body2">{(row as any)[id] ?? '—'}</Typography>
}

export function buildMaterialsColumns({
  onInfo,
  onView,
  columnOptions,
}: BuildMaterialsColumnsOptions): ColumnDef<MaterialRecord>[] {
  const actionsCol: ColumnDef<MaterialRecord> = {
    key: 'actions',
    header: '',
    width: 112,
    isActionColumn: true,
    render: (row) => <MaterialRowActions row={row} onInfo={onInfo} onView={onView} />,
  }

  if (columnOptions && columnOptions.length > 0) {
    const extraCols: ColumnDef<MaterialRecord>[] = columnOptions.map((col) => ({
      key: col.id,
      header: col.label,
      hideInMobileCard: col.id === 'name',
      render: renderMaterialCell(col),
    }))
    return [...extraCols, actionsCol]
  }

  const defaultCols: ColumnDef<MaterialRecord>[] = [
    {key: 'category', header: 'CATEGORIA', render: renderMaterialCell({id: 'category', label: 'CATEGORIA'})},
    {key: 'createdAt', header: 'CARICATO IL', render: renderMaterialCell({id: 'createdAt', label: 'CARICATO IL'})},
    {key: 'name', header: 'NOME', hideInMobileCard: true, render: renderMaterialCell({id: 'name', label: 'NOME'})},
    {key: 'type', header: 'TIPO', render: renderMaterialCell({id: 'type', label: 'TIPO'})},
    actionsCol,
  ]

  return defaultCols
}

// ── Links ──────────────────────────────────────────────────────────────────

interface BuildLinksColumnsOptions {
  columnOptions?: ColumnOption[]
}

const renderLinkCell = (col: ColumnOption) => (row: LinkRecord): React.ReactNode => {
  const {id} = col
  if (id === 'createdAt' || col.type === 'date') {
    return <Typography variant="body2">{formatDate((row as any)[id] ?? row.createdAt, col.format)}</Typography>
  }
  if (id === 'url') {
    return (
      <Typography
        variant="body2"
        sx={{maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
      >
        {row.url ?? '—'}
      </Typography>
    )
  }
  return <Typography variant="body2">{(row as any)[id] ?? '—'}</Typography>
}

export function buildLinksColumns({
  columnOptions,
}: BuildLinksColumnsOptions): ColumnDef<LinkRecord>[] {
  if (columnOptions && columnOptions.length > 0) {
    return columnOptions.map((col) => ({
      key: col.id,
      header: col.label,
      render: renderLinkCell(col),
    }))
  }

  return [
    {key: 'createdAt', header: 'CARICATO IL', render: renderLinkCell({id: 'createdAt', label: 'CARICATO IL'})},
    {key: 'name', header: 'TITOLO', render: renderLinkCell({id: 'name', label: 'TITOLO'})},
    {key: 'url', header: 'URL', render: renderLinkCell({id: 'url', label: 'URL'})},
  ]
}

// Colonna "specchiata" da un filtro hardcoded dei link, quando gli id differiscono.
const LINKS_FILTER_TO_COLUMN: Record<string, string> = {
  rangeDates: 'createdAt',
}

export function filterLinksFiltersByColumns(
  filters: FilterItem[],
  visibleColumns: ColumnDef<LinkRecord>[]
): FilterItem[] {
  const visibleKeys = new Set(visibleColumns.map((col) => col.key))
  return filters.filter((filter) => {
    const columnId = LINKS_FILTER_TO_COLUMN[filter.id] ?? filter.id
    return visibleKeys.has(columnId)
  })
}
